import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function TestTaking() {
  const { id } = useParams();
  const { API } = useAuth();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [startTime] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const { data } = await axios.get(`${API}/tests/${id}`, { withCredentials: true });
        setTest(data);
        setAnswers(new Array(data.questions.length).fill(-1));
      } catch (err) {
        alert("Failed to load test");
        navigate('/tests');
      }
    };
    fetchTest();
  }, [id, API, navigate]);

  const handleSubmit = async () => {
    if (answers.includes(-1) && !window.confirm("You have unanswered questions. Submit anyway?")) return;
    setSubmitting(true);
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    try {
      const { data } = await axios.post(`${API}/tests/${id}/submit`, { answers, time_taken_seconds: timeTaken }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      alert("Failed to submit test");
    } finally {
      setSubmitting(false);
    }
  };

  if (!test) return <div className="p-8 text-center tracking-widest uppercase animate-pulse text-primary font-bold">DOWNLOADING SCENARIO...</div>;

  if (result) {
    return (
      <div className="container py-8 max-w-4xl space-y-8" data-testid="test-result-page">
        <div className="text-center space-y-4 p-8 border border-border bg-secondary">
          <h1 className="text-4xl font-heading text-white uppercase tracking-widest">MISSION ACCOMPLISHED</h1>
          <div className="text-6xl font-black text-primary font-heading">{result.score} / {result.total}</div>
          <div className="text-sm font-bold uppercase tracking-widest text-muted-foreground">TIME: {result.time_taken_seconds}s</div>
          <Button onClick={() => navigate('/dashboard')} className="mt-4 rounded-none h-12 px-8 font-bold tracking-widest uppercase bg-primary text-white">RETURN TO COMMAND</Button>
        </div>
        <div className="space-y-6">
          <h2 className="text-2xl font-heading uppercase tracking-widest">AFTER ACTION REPORT</h2>
          {result.questions_with_answers.map((q, idx) => {
            const isCorrect = answers[idx] === q.correct_index;
            return (
              <Card key={idx} className={`rounded-none border-l-4 ${isCorrect ? 'border-l-chart-5 bg-card' : 'border-l-chart-2 bg-card'}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="font-bold text-lg">{idx + 1}. {q.question}</div>
                  <div className="space-y-2">
                    {q.options.map((opt, oIdx) => {
                      let bg = 'bg-secondary text-muted-foreground';
                      if (oIdx === q.correct_index) bg = 'bg-chart-5/20 text-chart-5 border border-chart-5/50';
                      else if (oIdx === answers[idx] && !isCorrect) bg = 'bg-chart-2/20 text-chart-2 border border-chart-2/50';
                      
                      return (
                        <div key={oIdx} className={`p-3 text-sm tracking-wide ${bg}`}>
                          {String.fromCharCode(65+oIdx)}. {opt}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs uppercase font-bold tracking-widest text-primary mb-1">AI EXPLANATION</p>
                    <p className="text-sm text-foreground/80">{q.explanation}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 max-w-4xl space-y-8" data-testid="test-taking-page">
      <div className="flex items-center justify-between border-b border-border pb-4 sticky top-[72px] bg-background/90 backdrop-blur z-40">
        <div>
          <h1 className="text-2xl font-heading text-primary tracking-widest uppercase">{test.title}</h1>
          <p className="text-xs font-bold text-muted-foreground tracking-widest uppercase">{answers.filter(a => a !== -1).length} / {test.questions.length} COMPLETED</p>
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="rounded-none font-bold tracking-widest bg-accent hover:bg-accent/80 text-white" data-testid="submit-test-btn">
          {submitting ? 'UPLOADING...' : 'END SIMULATION'}
        </Button>
      </div>

      <div className="space-y-12 pt-8">
        {test.questions.map((q, idx) => (
          <div key={idx} className="space-y-4" data-testid={`question-${idx}`}>
            <h3 className="text-xl font-bold font-body"><span className="text-primary">{idx + 1}.</span> {q.question}</h3>
            <div className="grid grid-cols-1 gap-3">
              {q.options.map((opt, oIdx) => (
                <button
                  key={oIdx}
                  onClick={() => {
                    const newAns = [...answers];
                    newAns[idx] = oIdx;
                    setAnswers(newAns);
                  }}
                  className={`text-left p-4 border transition-colors ${answers[idx] === oIdx ? 'border-primary bg-primary/10 text-white' : 'border-border bg-secondary hover:border-primary/50 text-muted-foreground'}`}
                  data-testid={`q${idx}-opt${oIdx}`}
                >
                  <span className="font-bold mr-2">{String.fromCharCode(65+oIdx)}.</span> {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}